import { createApi, retry } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery, getHeaders } from '@urc-ui/store/api/base';
import { HttpMethod } from '@urc-ui/store/api/types';
import { exponentialBackoffWithJitter } from '@flexible-reports/store/api/base';
import { CreateAnalysisRequest, AnalysisStatusResponse, GetAnalysisRequest, ListAnalysisRequest } from './api-types';
import { GetAnalysisResponse } from '@amzn/mads-insight-canvas';

function updateAnalysisStatus(draft: any, data: AnalysisStatusResponse) {
  if (!data.dataSources?.[0]?.id) {
    // one option is to also append to the list response once the api comes back. however, if there's no report id on
    // the analysis, the UI can't do anything with it. So just simply drop it.
    return;
  }
  const existingIndex = draft.findIndex((analysis: AnalysisStatusResponse) => analysis.dataSources[0]?.id === data.dataSources[0]?.id);
  if (existingIndex >= 0) {
    draft[existingIndex] = data;
  }
}

function addOrUpdateAnalysis(draft: any, optimisticAnalysis: AnalysisStatusResponse) {
  const existingIndex = draft.findIndex(
    (analysis: AnalysisStatusResponse) => analysis.dataSources[0]?.id === optimisticAnalysis.dataSources[0]?.id
  );

  if (existingIndex >= 0) {
    draft[existingIndex] = optimisticAnalysis;
  } else {
    draft.unshift(optimisticAnalysis);
  }
}

export const analysisApi = createApi({
  reducerPath: 'analysisApi',
  baseQuery: retry(
    axiosBaseQuery({
      baseUrl: '/reporting/api'
    }),
    exponentialBackoffWithJitter({ baseDelay: 50, maxDelay: 5000, maxRetries: 5 })
  ),
  tagTypes: ['AnalysisStatus'],
  endpoints: builder => ({
    createAnalysis: builder.mutation<AnalysisStatusResponse, CreateAnalysisRequest>({
      query: (request: CreateAnalysisRequest) => ({
        url: '/analysis',
        method: HttpMethod.PUT,
        data: request,
        headers: getHeaders()
      }),
      async onQueryStarted(request, { dispatch, getState, queryFulfilled }) {
        const optimisticAnalysis: AnalysisStatusResponse = {
          analysisId: `temp-${Date.now()}`,
          status: 'IN_PROGRESS',
          updatedAt: new Date().toISOString(),
          dataSources: [{ id: request.reportId, type: 'ADS', metadataId: '' }]
        };

        const queries = analysisApi.util
          .selectInvalidatedBy(getState(), [{ type: 'AnalysisStatus', id: 'LIST' }])
          .filter(query => query.endpointName === 'listAnalysis');

        const patchResults = queries.map(queryArgs =>
          dispatch(
            analysisApi.util.updateQueryData('listAnalysis', queryArgs.originalArgs, draft =>
              addOrUpdateAnalysis(draft, optimisticAnalysis)
            )
          )
        );

        try {
          const { data } = await queryFulfilled;
          queries.forEach(queryArgs => {
            dispatch(
              analysisApi.util.updateQueryData('listAnalysis', queryArgs.originalArgs, draft => {
                updateAnalysisStatus(draft, data);
              })
            );
          });
        } catch {
          patchResults.forEach(patch => patch.undo());
        }
      },
      invalidatesTags: () => [{ type: 'AnalysisStatus', id: 'LIST' }]
    }),
    getAnalysis: builder.query<GetAnalysisResponse, GetAnalysisRequest>({
      query: (request: GetAnalysisRequest) => ({
        url: `/analysis/${request.analysisId}`,
        method: HttpMethod.GET,
        headers: getHeaders()
      }),
      transformResponse: (response: any): GetAnalysisResponse => ({
        ...response,
        analysisSections: response.analysisSections.map((section: any) => {
          if (section.sectionType === 'CHART') {
            return {
              ...section,
              dataSeries: section.dataSeries.map((series: any) => ({
                id: series.id,
                name: series.name,
                ...(series.stringValues?.length > 0 ? { stringValues: series.stringValues } : {}),
                ...(series.numberValues?.length > 0 ? { numberValues: series.numberValues } : {})
              }))
            };
          } else {
            return {
              sectionType: 'MARKDOWN' as const,
              markdown: section.markdown
            };
          }
        })
      })
    }),
    listAnalysis: builder.query<AnalysisStatusResponse[], ListAnalysisRequest>({
      query: (request: ListAnalysisRequest) => ({
        url: `/analysis`,
        method: HttpMethod.POST,
        headers: getHeaders(),
        data: request
      }),
      providesTags: () => [{ type: 'AnalysisStatus', id: 'LIST' }]
    })
  })
});

export const { useCreateAnalysisMutation, useGetAnalysisQuery, useListAnalysisQuery } = analysisApi;
